import React from 'react';
import {
	Col,
	Form,
	Row,
	Skeleton,
} from 'antd';
import { Moment } from 'moment';
import {
	Currencies,
	FuelTypes,
	PortActionTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import DatePicker from '@client/components/DatePicker';
import Select from '@client/components/Select';
import Button from '@client/components/Button';
import { rules } from '@client/utils/form';
import { NullableBunkerRecord } from '@client/screens/estimates/details/helpers/types';
import { useMiscHandlers } from '@client/screens/estimates/details/context/hooks/useMiscHandlers';
import RobTable from './RobTable';

type Props = {
	eventType: string | null;
	fixtureCurrency: Values<typeof Currencies>;
	newRobs: NullableBunkerRecord[];
	onDeleteRob: (rob: boolean, record: NullableBunkerRecord) => void;
	setNewRobs: (records: NullableBunkerRecord[]) => void;
	onSaveRob: () => void;
	setBunkersReceived: (records: NullableBunkerRecord[]) => void;
	bunkersReceived: NullableBunkerRecord[];
	selectedDate: Moment | null;
	acceptableFuels: Array<Values<typeof FuelTypes>>;
	voyageDetails: GetVoyageDetailsResponse | undefined;
};

const RobEntryFormItems = ({
	eventType,
	fixtureCurrency,
	newRobs,
	onDeleteRob,
	setNewRobs,
	onSaveRob,
	setBunkersReceived,
	bunkersReceived,
	selectedDate,
	acceptableFuels,
	voyageDetails,
}: Props) => {
	const {
		portOptions
	} = useMiscHandlers();

	const canSetPricePerTon = eventType === PortActionTypes.DELIVERY ||
		eventType === PortActionTypes.REEVALUATION ||
		eventType === PortActionTypes.COMMENCEMENT;

	const canEdit = (
		eventType === PortActionTypes.BUNKER_STEM ||
		eventType === PortActionTypes.BUNKERS_ROB ||
		eventType === PortActionTypes.REDELIVERY ||
		eventType === PortActionTypes.REEVALUATION
	);

	let fuelOptions = Object.keys(FuelTypes).map((key) => ({
		label: FuelTypes[key as keyof typeof FuelTypes],
		value: key,
	}));

	if (voyageDetails?.linkedTcInVoyageId != null) {
		const uniqueFuels = Array.from(new Set([
			...(acceptableFuels ?? []),
			...(bunkersReceived ?? []).map((b) => b.fuelGrade),
		]));

		fuelOptions = uniqueFuels.map((f) => ({
			value: f,
			label: f,
		}));
	}

	return (
		<Row gutter={[16, 16]}>
			{eventType !== PortActionTypes.COMMENCEMENT && (
				<>
					<Col span={12}>
						<Form.Item
							name="event"
							shouldUpdate
							label="Event"
							rules={[rules.required]}
						>
							<Select
								disabled={eventType != null && !canEdit}
								showSearch
								options={[
									{ label: PortActionTypes.BUNKER_STEM, value: PortActionTypes.BUNKER_STEM },
									{ label: PortActionTypes.BUNKERS_ROB, value: PortActionTypes.BUNKERS_ROB },
								]}
								placeholder="Select Event"
							/>
						</Form.Item>
					</Col>
					{eventType !== PortActionTypes.BUNKERS_ROB && (
						<Col span={12}>
							<Form.Item
								name="port"
								shouldUpdate
								label="Port"
								rules={[rules.required]}
							>
								<Select
									disabled={!canEdit}
									showSearch
									options={portOptions}
									placeholder="Select Port"
								/>
							</Form.Item>
						</Col>
					)}
					<Col span={12}>
						<Form.Item
							name="date"
							label="Date"
							valuePropName="date"
							rules={[rules.required]}
						>
							<DatePicker
								time
								placeholder="Select date"
								defaultPickerValue={selectedDate ?? undefined}
								value={selectedDate}
								disabled={!canEdit}
							/>
						</Form.Item>
					</Col>
				</>
			)}
			<Col span={24}>
				{eventType == null ? (
					<>
						<p>* Select event type to enter ROB&apos;s</p>
						<Skeleton />
					</>
				) : (
					<>
						{(
							eventType === PortActionTypes.BUNKER_STEM ||
							eventType === PortActionTypes.REDELIVERY
						) &&
							(
								<RobTable
									showPricePerTon
									label={eventType === PortActionTypes.BUNKER_STEM ? 'Bunkers Received' : 'Bunkers Sold'}
									dataSource={bunkersReceived}
									onChange={setBunkersReceived}
									onDelete={(record) => onDeleteRob(false, record)}
									fuelOptions={fuelOptions}
									fixtureCurrency={fixtureCurrency}
								/>
							)
						}
						<RobTable
							showPricePerTon={canSetPricePerTon}
							label={`${eventType === PortActionTypes.BUNKER_STEM ? 'ROB (including bunkered quantities)' : 'ROB'}`}
							dataSource={newRobs}
							onChange={setNewRobs}
							onDelete={(record) => onDeleteRob(true, record)}
							fuelOptions={fuelOptions}
							fixtureCurrency={fixtureCurrency}
						/>
					</>
				)}
			</Col>
			<Col span={24}>
				<Button
					type="primary"
					onClick={onSaveRob}
				>
					Save & close
				</Button>
			</Col>
		</Row>
	);
};

export default RobEntryFormItems;
